import { useCallback, useEffect, useRef, useState } from 'react';
import { studioApi } from '../components/agentStudio/studioApi';

/**
 * "Regenerate from knowledge" (Robert, 02.11.2026): a new Business Context written from the agent's current
 * knowledge (POST /api/agent-studio/{bot}/business-context/generate). Every outcome comes back as a state; the text
 * is only a proposal that the page puts into its field, for the owner to check and save. Used by the Studio's Profile
 * tab and by Knowledge → Business Context.
 */
export default function useGenerateBusinessContext(botId, uiLang) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const current = useRef(botId);
  current.current = botId;
  useEffect(() => { setRunning(false); setResult(null); }, [botId]);

  const generate = useCallback(async () => {
    if (!botId) return null;
    const asked = botId;
    setRunning(true);
    setResult(null);
    let r;
    try {
      r = await studioApi.post(botId, '/business-context/generate', { ui_lang: uiLang });
    } catch (_) {
      r = { status: 'failed' };
    }
    if (r?.credits > 0) { // the portal's credit figures refresh
      try { window.dispatchEvent(new CustomEvent('ub:usage-updated')); } catch (_) { /* old browsers */ }
    }
    if (current.current !== asked) return null; // another agent was opened meanwhile
    setRunning(false);
    setResult(r);
    return r;
  }, [botId, uiLang]);

  const clear = useCallback(() => setResult(null), []);
  return { running, result, generate, clear };
}

/** What to tell the owner about an outcome: { tone: 'ok' | 'warn', text, knowledge } (knowledge: offer the way to
 *  add knowledge), or null. AI credits are named only while they are on for the agent. */
export function generateMessage(t, r) {
  if (!r) return null;
  switch (r.status) {
    case 'ok':
      return { tone: 'ok', text: r.credits > 0 && r.credits_mode === 'on'
        ? t('business_context_generated_credits', { count: r.credits }) : t('business_context_generated') };
    case 'no_knowledge':
      return r.pending
        ? { tone: 'warn', text: t('business_context_gen_pending') }
        : { tone: 'warn', text: t('business_context_gen_no_knowledge'), knowledge: true };
    case 'too_little_knowledge':
      return { tone: 'warn', knowledge: true,
        text: [r.note || t('business_context_gen_too_little'), r.pending ? t('business_context_gen_pending') : '']
          .filter(Boolean).join(' ') };
    case 'no_credits':
      return { tone: 'warn', text: t('business_context_gen_no_credits', { needed: r.needed, available: r.available }) };
    case 'busy':
      return { tone: 'warn', text: t('business_context_gen_busy') };
    default:
      return { tone: 'warn', text: t('business_context_gen_failed') };
  }
}
